import type { RumEvent } from '@datadog/browser-rum';
import { normalizeEndpoint } from '../error-tracking/normalize-endpoint';
import { isRecentlyReported } from '../error-tracking/recently-reported';
import { rememberUserRequestTraceId } from './request-trace-ids';

export type PathNormalizer = (path: string) => string;
const keepPath: PathNormalizer = path => path;

/**
 * An absolute URL or a `/path` followed by a query or fragment, inside free text. The query ends at
 * whitespace, a quote, or a trailing ":" (so "Http failure response for <url>: 500" keeps its colon).
 */
const QUERY_IN_TEXT = /(https?:\/\/[^\s?#"']+|(?<![\w:/.])\/[^\s?#"']*)[?#][^\s"']*?(?=:?(?:\s|["']|$))/gi;
const EXTENSION_FRAME = /(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i;
const NOISE_MESSAGES = [
  /ResizeObserver loop/i,
  // Firefox's AbortError for promises still pending while the page unloads.
  /browsing context is going away/i,
];
const UNHANDLED_REJECTION_PREFIX = /^Uncaught \(in promise\):?\s*/;

/** Removes query strings and fragments from every URL or `/path` in free text, such as an error message. */
export function stripQueryStrings(text: string): string {
  return text.replaceAll(QUERY_IN_TEXT, '$1');
}

/** Drops the query and hash, then normalizes the path (e.g. removes the restaurant number). */
export function scrubUrl(value: string, normalizePath: PathNormalizer = keepPath): string {
  try {
    const url = new URL(value);
    return `${url.origin}${normalizePath(url.pathname)}`;
  } catch {
    return normalizePath(value.replace(/[?#].*$/, ''));
  }
}

/**
 * A request URL reduced to its host and a path template (`/api/location/:id/eventStats`), so resources
 * group by endpoint and the user name some paths carry never leaves the browser.
 */
export function scrubResourceUrl(value: string): string {
  try {
    const { protocol } = new URL(value);
    const { host, endpoint } = normalizeEndpoint(value);
    return `${protocol}//${host}${endpoint}`;
  } catch {
    return value.replace(/[?#].*$/, '');
  }
}

/**
 * True for errors RUM collects on its own that we already reported: an HTTP failure
 * `RumHttpErrorInterceptor` sent, or a message `markReported` recorded, reaching the
 * unhandled-rejection hook again uncaught.
 */
function isDuplicateReport(message: string): boolean {
  return /^Http failure response for /.test(message)
    || isRecentlyReported(message.replace(UNHANDLED_REJECTION_PREFIX, ''));
}

function scrubError(event: Extract<RumEvent, { type: 'error' }>): boolean {
  const { message, stack } = event.error;
  if (NOISE_MESSAGES.some(pattern => pattern.test(message ?? ''))) {
    return false;
  }
  if (stack && EXTENSION_FRAME.test(stack.split('\n')[1] ?? '')) {
    return false;
  }
  if (event.error.source !== 'custom' && isDuplicateReport(message ?? '')) {
    return false;
  }
  event.error.message = stripQueryStrings(message ?? '');

  const context = event.context as Record<string, unknown> | undefined;

  // `addError` always records a handled error; Error Tracking filters on `error.handling`.
  if (context?.unhandled === true) {
    (event.error as { handling?: string }).handling = 'unhandled';
    delete context.unhandled;
  }

  // Custom grouping: Error Tracking groups by stack otherwise, and every HTTP failure shares one.
  if (typeof context?.groupingHash === 'string') {
    event.error.fingerprint = context.groupingHash;
    delete context.groupingHash;
  }
  return true;
}

/**
 * The single `beforeSend`. Views are grouped by route, never by restaurant, resources by endpoint,
 * and no query strings leave the browser. Returns false only for noise and duplicate error reports.
 */
export function scrubRumEvent(event: RumEvent, normalizePath: PathNormalizer = keepPath): boolean {
  if (event.type === 'resource') {
    // Before the URL is scrubbed: the user request is recognised by its path.
    rememberUserRequestTraceId(event.resource.method ?? 'GET', event.resource.url, event._dd?.trace_id);
  }
  if (event.type === 'error' && !scrubError(event)) {
    return false;
  }

  if (event.view) {
    event.view.url = scrubUrl(event.view.url, normalizePath);
    if (event.view.referrer) {
      event.view.referrer = scrubUrl(event.view.referrer, normalizePath);
    }
    if (event.view.name) {
      event.view.name = normalizePath(event.view.name.replace(/[?#].*$/, ''));
    }
  }
  if (event.type === 'resource') {
    event.resource.url = scrubResourceUrl(event.resource.url);
  }
  return true;
}
